/**
 * Flight Recorder Overlay - Debug tool for onboarding/tour issues
 * 
 * Shows real-time:
 * - Current onboarding state
 * - Auth/profile hydration status
 * - Tour active/step
 * - Navigate() call log
 * - All state transitions
 * 
 * Enable: localStorage.setItem('therappi-flight-recorder', 'true')
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getOnboardingState, 
  isAuthHydrated, 
  isProfileHydrated, 
  getTourStep,
  getFlightLog,
  type OnboardingStage
} from '@/hooks/useOnboardingStateMachine';
import { isTourActiveGlobal, isOnboardingTransitioning } from '@/hooks/useTourController';
import { useLocation } from 'react-router-dom';

const SHOW_KEY = 'therappi-flight-recorder';

function shouldShow(): boolean {
  try {
    return localStorage.getItem(SHOW_KEY) === 'true';
  } catch {
    return false;
  }
}

const stateColors: Record<OnboardingStage, string> = {
  hydrating: 'bg-yellow-500',
  pre_auth_welcome: 'bg-purple-500',
  pre_auth_typewriter: 'bg-violet-500',
  auth: 'bg-blue-500',
  conversational: 'bg-orange-500',
  tour: 'bg-pink-500',
  app: 'bg-green-500',
};

export function FlightRecorderOverlay() {
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<OnboardingStage>('hydrating');
  const [logs, setLogs] = useState<ReturnType<typeof getFlightLog>>([]);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const location = useLocation();
  const logsEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    setShow(shouldShow());
  }, []);
  
  useEffect(() => {
    if (!show) return;
    
    const interval = setInterval(() => {
      setState(getOnboardingState());
      setLogs(getFlightLog().slice(-50)); // Last 50 entries
      setLastUpdate(Date.now());
    }, 500);
    
    return () => clearInterval(interval);
  }, [show]);
  
  // Auto-scroll to bottom of logs
  useEffect(() => {
    if (expanded && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, expanded]);
  
  if (!show) return null;
  
  const authHydrated = isAuthHydrated();
  const profileHydrated = isProfileHydrated();
  const tourActive = isTourActiveGlobal();
  const transitioning = isOnboardingTransitioning();
  const tourStep = getTourStep();
  
  return (
    <>
      {/* Floating button */}
      <motion.button
        onClick={() => setExpanded(!expanded)}
        className={`fixed bottom-20 left-4 z-[9999] px-3 py-2 rounded-full text-white text-xs font-mono shadow-lg ${stateColors[state]}`}
        whileTap={{ scale: 0.95 }}
      >
        ✈️ {state} {tourActive ? `(T${tourStep})` : ''}
      </motion.button>
      
      {/* Expanded panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            className="fixed left-4 bottom-32 z-[9999] w-80 max-h-[60vh] bg-black/95 text-white rounded-lg shadow-2xl overflow-hidden font-mono text-[10px]"
          >
            {/* Header */}
            <div className="p-3 border-b border-white/20 bg-black sticky top-0">
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-sm">Flight Recorder</span>
                <button onClick={() => setExpanded(false)} className="text-white/60 hover:text-white">✕</button>
              </div>
              
              {/* Status grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${authHydrated ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span>Auth</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${profileHydrated ? 'bg-green-400' : 'bg-red-400'}`} />
                  <span>Profile</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${tourActive ? 'bg-pink-400 animate-pulse' : 'bg-gray-400'}`} />
                  <span>Tour {tourActive ? `(${tourStep}/13)` : ''}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${transitioning ? 'bg-yellow-400 animate-pulse' : 'bg-gray-400'}`} />
                  <span>Transitioning</span>
                </div>
              </div>
              
              <div className="mt-2 flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded ${stateColors[state]} text-white font-bold`}>
                  {state.toUpperCase()}
                </span>
                <span className="text-white/60">{location.pathname}</span>
              </div>
            </div>
            
            {/* Logs */}
            <div className="overflow-y-auto max-h-[40vh] p-2 space-y-1">
              {logs.map((log, i) => (
                <div 
                  key={i} 
                  className={`p-1.5 rounded text-[9px] ${
                    log.event.includes('NAV_BLOCKED') ? 'bg-red-900/50 border-l-2 border-red-500' :
                    log.event.includes('NAV_ALLOWED') ? 'bg-green-900/30' :
                    log.event.includes('STATE_CHANGE') ? 'bg-yellow-900/50 border-l-2 border-yellow-500' :
                    log.event.includes('TOUR') ? 'bg-pink-900/30' :
                    'bg-white/5'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-bold text-white/90">{log.event}</span>
                    <span className="text-white/40">{log.timestamp.split('T')[1].slice(0, 8)}</span>
                  </div>
                  <div className="text-white/60 break-all">
                    {JSON.stringify(log.data).slice(0, 200)}
                  </div>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
